import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useReducer,
    useCallback,
    useState,
} from "react";
import { SplashScreen } from "src/components/splash-screen";
import { Household, UserDataObj } from "src/types/snugtotal";
import { GenericPath, useV3GenericGetData, useV3Update } from "src/api/snugtotal/estate_app";
import { useRouter } from "src/hooks/use-router";
import { usePathname } from "src/hooks/use-pathname";

interface HouseholdProviderProps {
    children: ReactNode;
    userData: UserDataObj | null;
    ownerUserData: UserDataObj | null;
    updateProHouseholdContext: (household: Household) => void;
}

// create experience flag enum
export enum ExperienceFlag {
    HEAD_OF_HOUSEHOLD = "HEAD_OF_HOUSEHOLD",
    HOUSEHOLD_MEMBER = "HOUSEHOLD_MEMBER",
    READONLY = "READONLY",
    PROFESSIONAL = "PROFESSIONAL",
}

type HouseholdStoreAction =
    | { type: "SET_HOUSEHOLDS"; payload: Household[] }
    | { type: "SET_SELECTED_HOUSEHOLD_ID"; payload: string | null }
    | { type: "SET_LOADING"; payload: boolean };

interface HouseholdContextState {
    households: Household[];
    selectedHousehold: Household | null;
    selectedHouseholdId: string | null;
    loading: boolean;
    selectedUdId: string | null;
    headOfHouseholDataSaving: boolean;
}

interface HouseholdContextValue extends HouseholdContextState {
    experienceFlag: ExperienceFlag;
    selectHousehold: (householdId: string) => void;
    setSelectedUdId: (udid: string) => void;
    refreshHouseholds: () => Promise<void>;
    onboardingHandleEditHoHUserData: (hoh_ud_id: string, userData: UserDataObj) => Promise<void>;
}

const householdReducer = (
    state: HouseholdContextState,
    action: HouseholdStoreAction
): HouseholdContextState => {
    switch (action.type) {
        case "SET_HOUSEHOLDS": {
            const households = action.payload;
            const selectedHousehold =
                households?.find((h) => h.household_role?.id === state.selectedHouseholdId) || households[0] || null;
            return {
                ...state,
                households,
                selectedHousehold,
                selectedHouseholdId: selectedHousehold?.household_role?.id || null,
                selectedUdId: selectedHousehold?.head_of_household?.ud_id || null,
            };
        }
        case "SET_SELECTED_HOUSEHOLD_ID": {
            const selectedHousehold =
                state.households?.find((h) => h.household_role?.id === action.payload) || state.households[0] || null;
            return {
                ...state,
                selectedHouseholdId: action.payload,
                selectedHousehold,
                selectedUdId: selectedHousehold?.head_of_household?.ud_id || null,
            };
        }
        case "SET_LOADING":
            return { ...state, loading: action.payload };
        default:
            return state;
    }
};

const HouseholdContext = createContext<HouseholdContextValue>({
    households: [],
    selectedHousehold: null,
    selectedHouseholdId: null,
    loading: true,
    selectedUdId: null,
    experienceFlag: ExperienceFlag.HEAD_OF_HOUSEHOLD,
    headOfHouseholDataSaving: false,
    selectHousehold: () => { },
    setSelectedUdId: () => { },
    refreshHouseholds: async () => { },
    onboardingHandleEditHoHUserData: async () => { },
});

export const HouseholdProvider: FC<HouseholdProviderProps> = (props) => {
    const { children, userData, ownerUserData, updateProHouseholdContext } = props;
    const router = useRouter();
    const pathname = usePathname();
    const [state, dispatch] = useReducer(householdReducer, {
        households: [],
        selectedHousehold: null,
        selectedHouseholdId: null,
        loading: true,
        selectedUdId: null,
        headOfHouseholDataSaving: false,
    });
    const [headOfHouseholDataSaving, setHeadOfHouseholDataSaving] = useState(false);
    const [experienceFlag, setExperienceFlag] = useState<ExperienceFlag>(ExperienceFlag.HEAD_OF_HOUSEHOLD);
    const [selectedUdId, setSelectedUdId] = useState<string | null>(null);

    const getHouseholds = useV3GenericGetData<Household[]>(
        GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
    );

    const getHousehold = useV3GenericGetData<Household>(
        GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
    );

    const updateUserData = useV3Update<UserDataObj>(
        GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
    );

    const refreshHouseholds = useCallback(async () => {
        try {
            const response = await getHouseholds({
                ud_id: userData?.ud_id || "",
                entity: "households",
            }, { expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing" });
            dispatch({ type: "SET_HOUSEHOLDS", payload: response });
        } catch (error) {
            console.error(error);
        } finally {
        }
    }, [userData?.ud_id, getHouseholds]);

    const onboardingHandleEditHoHUserData = useCallback(
        async (
            hoh_ud_id: string,
            userData: UserDataObj,
        ): Promise<void> => {
            if (!hoh_ud_id) {
                console.error("No user data id provided");
                return Promise.reject("No user data id provided");
            }

            try {
                setHeadOfHouseholDataSaving(true);
                const params = { ud_id: hoh_ud_id, entity: "role-target-user-data" };
                const expandQuery = { expand: "professional_group_role,household" };
                await updateUserData(
                    { ...params, entity_id: hoh_ud_id },
                    userData,
                    true,
                    expandQuery
                );

                await refreshHouseholds();

            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                setHeadOfHouseholDataSaving(false);
            }
        },
        [updateUserData, refreshHouseholds]
    );

    useEffect(() => {
        let isMounted = true;

        const fetchSingleHousehold = async (household_id: string): Promise<Household | null> => {
            try {
                if (!household_id) {
                    return null;
                }
                const response = await getHousehold({
                    ud_id: userData?.ud_id || "",
                    entity: "household",
                    entity_id: household_id,
                }, { expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing" });
                if (sessionStorage.getItem("h_cc")) {
                    updateProHouseholdContext(response);
                }
                if (isMounted) {
                    await dispatch({ type: "SET_HOUSEHOLDS", payload: [response] });
                    return response;
                }
                return null;
            } catch (error) {
                return null;
            } finally {
                if (isMounted) {
                    dispatch({ type: "SET_LOADING", payload: false });
                }
            }
        };

        const fetchHouseholds = async () => {
            dispatch({ type: "SET_LOADING", payload: true });
            let isMounted = true;

            try {
                const response = await getHouseholds({
                    ud_id: userData?.ud_id || "",
                    entity: "households",
                }, { expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing" });
                if (isMounted) {
                    dispatch({ type: "SET_HOUSEHOLDS", payload: response });
                    //   set selected household to the first household
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) {
                    dispatch({ type: "SET_LOADING", payload: false });
                }
            }
        };
        const proclient_ud_id = sessionStorage.getItem("h_cc");
        if (userData?.ud_id && !proclient_ud_id) {
            fetchHouseholds();
        } else if (userData?.ud_id && proclient_ud_id) {
            fetchSingleHousehold(proclient_ud_id);
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.ud_id]);

    useEffect(() => {
        if (state.households && state.households.length > 0) {
            dispatch({ type: "SET_SELECTED_HOUSEHOLD_ID", payload: state.households[0].household_role?.id || null });
        }
    }, [state.households]);

    const selectHousehold = useCallback((householdId: string) => {
        dispatch({ type: "SET_SELECTED_HOUSEHOLD_ID", payload: householdId });
    }, []);

    const check = useCallback(() => {
        if (userData?.ud_id && userData.ud_id === state.selectedHousehold?.head_of_household?.ud_id && state.selectedHousehold?.head_of_household?.show_household_onboarding_requirement) {
            if (pathname !== "/onboarding") {
                router.replace("/onboarding");
            }
        }
    }, [userData, state.selectedHousehold, router, pathname]);

    useEffect(() => {
        if (userData && state.selectedHousehold && experienceFlag !== ExperienceFlag.READONLY) {
            check();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, state.selectedHousehold, experienceFlag]);

    // Sync reducer state to local state
    useEffect(() => {
        if (state.selectedUdId !== selectedUdId) {
            setSelectedUdId(state.selectedUdId);
        }
    }, [state.selectedUdId, selectedUdId]);

    // When selectedUdId changes, keep in sync with reducer
    useEffect(() => {
        // No-op for now, but could dispatch to reducer if needed
    }, [selectedUdId]);

    useEffect(() => {
        if (ownerUserData?.ud_id && ownerUserData.ud_id === state.selectedHousehold?.head_of_household?.ud_id) {
            setExperienceFlag(ExperienceFlag.HEAD_OF_HOUSEHOLD);
        } else if (ownerUserData?.ud_id && ownerUserData.ud_id === state.selectedHousehold?.professional_advisor?.ud_id && ownerUserData.ud_id !== state.selectedHousehold?.head_of_household?.ud_id) {
            setExperienceFlag(ExperienceFlag.PROFESSIONAL);
        } else if (ownerUserData?.ud_id && state.selectedHousehold?.household_member_roles.find(member => member.role_target_user_data_ud_id === ownerUserData.ud_id && member.is_household_member)) {
            setExperienceFlag(ExperienceFlag.HOUSEHOLD_MEMBER);
        } else if (ownerUserData?.ud_id && state.selectedHousehold?.household_member_roles.find(member => member.role_target_user_data_ud_id === ownerUserData.ud_id && member.is_read_only)) {
            setExperienceFlag(ExperienceFlag.READONLY);
        } else {
            setExperienceFlag(ExperienceFlag.HEAD_OF_HOUSEHOLD);
        }
    }, [ownerUserData, state.selectedHousehold]);

    if (state.loading) {
        return <SplashScreen />;
    }

    return (
        <HouseholdContext.Provider
            value={{
                ...state,
                experienceFlag,
                selectedUdId,
                headOfHouseholDataSaving,
                selectHousehold,
                setSelectedUdId,
                refreshHouseholds,
                onboardingHandleEditHoHUserData,
            }}
        >
            {children}
        </HouseholdContext.Provider>
    );
};

export default function useHousehold() {
    return useContext(HouseholdContext);
}

export const HouseholdConsumer = HouseholdContext.Consumer; 